import { effect } from "@preact/signals-core";

type Cleanup = () => void;
type EffectFn = () => void | Cleanup;

export interface Scope {
  effect(fn: EffectFn): void;
  cleanup(fn: Cleanup): void;
  dispose(): void;
}

export function createScope(): Scope {
  const disposers: Cleanup[] = [];
  let disposed = false;

  return {
    effect(fn) {
      if (disposed) throw new Error("cannot register an effect on a disposed scope");
      disposers.push(effect(fn));
    },
    cleanup(fn) {
      if (disposed) throw new Error("cannot register cleanup on a disposed scope");
      disposers.push(fn);
    },
    dispose() {
      disposed = true;
      while (disposers.length > 0) disposers.pop()!();
    },
  };
}

let activeScope: Scope | null = null;

export function currentScope(): Scope {
  if (!activeScope) {
    throw new Error("binding used outside effectScope() — views must call bindings synchronously");
  }
  return activeScope;
}

export interface ScopedView {
  node: Node;
  dispose: () => void;
}

export function effectScope(view: () => Node): ScopedView {
  const scope = createScope();
  const previous = activeScope;
  activeScope = scope;
  let node: Node;
  try {
    node = view();
  } finally {
    activeScope = previous;
  }
  return { node, dispose: () => scope.dispose() };
}
